<template>
  <div class="study-container">
    <div class="study-details">
      <div class="start-stop-details">
        <PopoverTimestamp
          :value="startTimestamp"
          :label="$t('widget.currentReadings.studyStart')"
          :invalidText="$t('map.popover.valueBlank')"
          customClass="small-subtitle"
          maxWidth="300"
          :tooltipText="startTimestampTooltipText"
          dataCy="studyStart"
        />
        <PopoverTimestamp
          :value="stopTimestamp"
          :label="$t('widget.currentReadings.studyStop')"
          :invalidText="$t('map.popover.valueBlank')"
          customClass="small-subtitle"
          maxWidth="300"
          dataCy="studyStop"
        />
      </div>
      <PopoverText
        :value="duration"
        :label="$t('widget.currentReadings.studyDuration')"
        :invalidText="$t('map.popover.valueBlank')"
        customClass="small-subtitle"
        maxWidth="300"
        dataCy="studyDuration"
      />
    </div>
    <OneMeasurementMonitoring
      :deviceId="deviceId"
      :selectedParameter="selectedParameter"
      :readings="readings"
      size="medium"
    />
    <MeasurementStatistics
      :deviceId="deviceId"
      :measurementStatisticsReadings="measurementStatisticsReadings"
      :selectedParameter="selectedParameter"
    />
    <DeviceConditions :deviceId="deviceId" :lastTelemetryTimestamp="lastTelemetryTimestamp" />
  </div>
</template>
<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { utils } from '@fusion/saas-components-v3'
import MeasurementStatistics from '@/components/widgets/currentReadings/content/MeasurementStatistics.vue'
import OneMeasurementMonitoring from '@/components/devicePopover/layouts/OneMeasurementMonitoring.vue'
import PopoverTimestamp from '@/components/devicePopover/content/popoverTimestamp.vue'
import PopoverText from '@/components/devicePopover/content/popoverText.vue'
import DeviceConditions from '@/components/widgets/currentReadings/content/DeviceConditions.vue'
import { activeLastDay } from '@/services/device-status'
import { MeasurementType } from '@/services/device-measurements'
import { toMs } from '@/components/devicePopover/utils'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import type { SensorReadings } from '@/services/device-service/devices'
import type { GetterByDeviceId, MeasurementStats } from '../types'

const BUFFER_TIME = 5000

export default defineComponent({
  name: 'StudyContent',
  components: {
    MeasurementStatistics,
    OneMeasurementMonitoring,
    PopoverTimestamp,
    PopoverText,
    DeviceConditions
  },
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    selectedParameter: {
      type: Number as PropType<MeasurementType>,
      default: MeasurementType.Pm25
    }
  },
  data() {
    return {
      duration: null as string | null,
      durationInterval: null as NodeJS.Timeout | null
    }
  },
  watch: {
    studyStartTimeMs(): void {
      this.startDurationPolling()
    },
    hasStudyEnded(hasStudyEnded: boolean): void {
      if (hasStudyEnded) {
        this.stopDurationInterval()
        this.calculateDurationTimestamp()
      }
    }
  },
  mounted() {
    this.startDurationPolling()
  },
  unmounted() {
    this.stopDurationInterval()
  },
  computed: {
    sensorReadings(): SensorReadings {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_READINGS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<SensorReadings>
      return getter(this.deviceId)
    },
    measurementStatisticsReadings(): MeasurementStats {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LATEST_STUDY_MEASUREMENT_STATISTICS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<MeasurementStats>
      return getter(this.deviceId)
    },
    wasDeviceConnectedAfterStart(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_CONNECTED_AFTER_START}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    isLatestDeviceStudyIncomplete(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_INCOMPLETE}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    isLatestDeviceStudyInProgress(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_IN_PROGRESS}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    isLatestStudyComplete(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.IS_LATEST_DEVICE_STUDY_COMPLETE}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    latestStudyMaxTimestamp(): string | null {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LATEST_STUDY_MAX_TIMESTAMP_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string | null>
      return getter(this.deviceId)
    },
    latestStudyDuration(): number | undefined {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LATEST_STUDY_DURATION_BY_DEVICE_ID}`
      ] as GetterByDeviceId<number | undefined>
      return getter(this.deviceId)
    },
    latestStudySampleInterval(): number | undefined {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LATEST_STUDY_SAMPLING_INTERVAL_BY_DEVICE_ID}`
      ] as GetterByDeviceId<number | undefined>
      return getter(this.deviceId)
    },
    getLatestStudyStartTime(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LATEST_STUDY_START_TIME_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    lastTelemetryTimestamp(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    isDeviceConnected(): boolean {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
      ] as GetterByDeviceId<boolean>
      return getter(this.deviceId)
    },
    telemTxType(): number | null {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_TELEM_TX_TYPE_BY_DEVICE_ID}`
      ] as GetterByDeviceId<number | null>
      return getter(this.deviceId)
    },
    startTimestamp(): string | null {
      return (!this.wasDeviceConnectedAfterStart && this.isTheDeviceActiveLast24hr) ||
        this.isStudyStarting
        ? this.getLatestStudyStartTime
        : null
    },
    startTimestampTooltipText(): string {
      if (this.wasDeviceConnectedAfterStart) {
        return this.$t('widget.currentReadings.studyStartConnectedAfterStart')
      } else if (this.isLatestDeviceStudyIncomplete) {
        return this.$t('widget.currentReadings.studyIncompleteData')
      }
      return ''
    },
    stopTimestamp(): string | null {
      return this.isLatestStudyComplete && this.isTheDeviceActiveLast24hr
        ? this.latestStudyMaxTimestamp
        : null
    },
    readings(): SensorReadings {
      return this.sensorReadings
    },
    // True if device has NOT been active within the last day
    isTheDeviceActiveLast24hr(): boolean {
      return activeLastDay(this.lastTelemetryTimestamp)
    },
    // True if the device has never reported telemetry and has no study start time
    hasNoSignals(): boolean {
      return (
        this.lastTelemetryMs == null &&
        this.studyStartTimeMs == null &&
        !this.isTheDeviceActiveLast24hr
      )
    },
    // True if device has been inactive for 24h AFTER the study started
    isInactiveSinceStudyStart(): boolean {
      return (
        !this.isTheDeviceActiveLast24hr &&
        this.lastTelemetryMs != null &&
        this.studyStartTimeMs != null &&
        this.lastTelemetryMs > this.studyStartTimeMs
      )
    },
    // Study start timestamp normalized to ms
    studyStartTimeMs(): number | null {
      return toMs(this.getLatestStudyStartTime)
    },
    // Last telemetry timestamp normalized to ms
    lastTelemetryMs(): number | null {
      return toMs(this.lastTelemetryTimestamp)
    },
    /**
     * True when the study has just started but no telemetry has arrived yet,
     * or when the start timestamp is later than the last telemetry timestamp.
     * This helps us decide if we should begin calculating duration locally.
     */
    isStudyStarting() {
      return (
        this.studyStartTimeMs != null &&
        (this.lastTelemetryMs == null || this.studyStartTimeMs > this.lastTelemetryMs)
      )
    },
    hasStudyEnded(): boolean {
      return !this.isLatestDeviceStudyInProgress || this.isLatestStudyComplete
    }
  },
  methods: {
    startDurationPolling(): void {
      this.calculateDurationTimestamp()
      this.startDurationInterval()
    },
    calculateDurationTimestamp(): void {
      const hasBit = (n: number, mask: number): boolean => (n & mask) === mask
      const txType = this.telemTxType
      const latestDurationMs = this.latestStudyDuration
      const isConnected = this.isDeviceConnected
      const uploadingOffline = typeof txType === 'number' && hasBit(txType, 2)
      const liveStreaming = typeof txType === 'number' && hasBit(txType, 4)
      // Inactivity conditions
      // - hasNoSignals: brand-new device (no study, no telemetry, inactive last day)
      // - isInactiveSinceStudyStart: telemetry stopped after study started (inactive last day)
      const inactive24 = this.hasNoSignals || this.isInactiveSinceStudyStart

      // --- Guards ---
      // No valid start timestamp
      if (this.studyStartTimeMs == null) {
        this.duration = null
        return
      }
      // While uploading offline studies, hide duration
      if (uploadingOffline) {
        this.duration = this.hasStudyEnded
          ? utils.common.formatDuration(latestDurationMs ?? 0)
          : null
        return
      }
      // If study finished or not in progress:
      // - If inactive for 24h (any of the inactivity cases), hide duration (stale)
      // - Otherwise trust backend-provided duration
      if (this.hasStudyEnded) {
        this.duration = inactive24 ? null : utils.common.formatDuration(latestDurationMs ?? 0)
        return
      }
      // --- In-progress case ---
      // If connected, live streaming, or just starting -> compute from local clock,
      // capped by last telemetry + (sampleInterval + BUFFER_TIME)
      if (isConnected || liveStreaming || this.isStudyStarting) {
        const now = Date.now()
        const elapsed = Math.max(now - this.studyStartTimeMs, 0)

        // Cap by last telemetry + buffer (protects against clock drift and late packets)
        const sampleIntervalMs = (this.latestStudySampleInterval ?? 0) * 1000
        const bufferMs = sampleIntervalMs + (typeof BUFFER_TIME === 'number' ? BUFFER_TIME : 0)

        let effective = elapsed
        // Cap by last telemetry + buffer (protects against clock drift and late packets)
        if (this.lastTelemetryMs != null && this.lastTelemetryMs >= this.studyStartTimeMs) {
          const lastTelemElapsed = Math.max(
            this.lastTelemetryMs - this.studyStartTimeMs + bufferMs,
            0
          )
          effective = Math.min(elapsed, lastTelemElapsed)
        }

        this.duration = utils.common.formatDuration(effective)
      }
    },
    startDurationInterval(): void {
      if (!this.hasStudyEnded && this.durationInterval == null) {
        this.durationInterval = setInterval(this.calculateDurationTimestamp, 1000)
      } else {
        this.calculateDurationTimestamp()
      }
    },
    stopDurationInterval(): void {
      if (this.durationInterval) {
        clearInterval(this.durationInterval)
        this.durationInterval = null
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.study-details {
  display: flex;
  justify-content: space-between;
}
.start-stop-details {
  display: flex;
  flex-direction: column;
  :deep(.small-subtitle) {
    display: flex;
    justify-content: space-between;
    max-height: 17px !important;
    padding-bottom: 0px !important;
  }
}
</style>
